import { CheckOutlined, LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons'
import { useDebounce } from 'ahooks'
import { AutoComplete, Button, Tag, message } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetFrequentSkillsQuery, useGetSkillSearchQuery, useSkillCreateMutation } from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage3: React.FC<OnboardingPageProps> = ({
  onPrevious,
  onNext,
  userInfo,
  onUserInfoChange,
  viewer,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 500 })
  const userId = viewer.userId

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const { data: frequentSkillData } = useGetFrequentSkillsQuery({
    skip: userInfo.skills.length > 0,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        top: 24,
      },
      userId,
    },
  })

  const { data } = useGetSkillSearchQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        search: debouncedSearch,
      },
      userId,
    },
  })

  const [skillCreate] = useSkillCreateMutation({
    onCompleted(data) {
      const skill = data.skillCreate
      if (skill) {
        onUserInfoChange((draft) => {
          draft.skills = [...draft.skills.slice(), { ...skill, isSelected: true }]
        })
        setSearch('')
        message.info('添加技能成功')
      } else {
        message.warn('添加技能失败，可能原因：已存在')
      }
    },
  })

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '3',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  React.useEffect(() => {
    if (frequentSkillData) {
      onUserInfoChange((draft) => {
        draft.skills = frequentSkillData.skills.map((skill) => ({ ...skill, isSelected: false }))
      })
    }
  }, [frequentSkillData])

  const skillCandidates = data?.skills.slice(0, 6) || []

  const options =
    skillCandidates.length > 0
      ? skillCandidates.map((value) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <span>{value.name}</span>
            </div>
          ),
          value: value.skillId,
        }))
      : [{ label: <span>{search}</span>, value: 0 }]

  const searchComponent = (
    <AutoComplete
      defaultActiveFirstOption={true}
      filterOption={false}
      value={search}
      placeholder={t('user.info.skill.searchPlaceholder')}
      style={{ width: '100%' }}
      onSelect={(id: any) => {
        if (Number(id) === 0 && search !== '') {
          skillCreate({
            variables: {
              name: search,
            },
          })
          return
        }

        const candidate = skillCandidates.find((candidate) => candidate.skillId === Number(id))
        if (candidate) {
          setSearch(candidate.name)
          // 选择的技能已在添加列表

          if (userInfo.skills.some((skill) => skill.skillId === Number(id))) {
            onUserInfoChange((draft) => {
              const skill = draft.skills.find((skill) => skill.skillId === Number(id))
              skill!.isSelected = !skill!.isSelected
            })
          } else {
            onUserInfoChange((draft) => {
              draft.skills.push({
                ...skillCandidates.find((candidate) => candidate.skillId === Number(id))!,
                isSelected: true,
              })
            })
          }
        }
      }}
      options={options}
      onSearch={(value) => {
        setSearch(value)
      }}
    />
  )

  const candidates = (
    <div className={s.candidates}>
      {userInfo.skills.map((skill) => {
        const isSelected = userInfo.skills.some((s) => s.skillId === skill.skillId && s.isSelected)
        const Icon = isSelected ? CheckOutlined : PlusOutlined
        return (
          <Tag
            color={isSelected ? '#48bbb5' : undefined}
            style={{ backgroundColor: isSelected ? '#48bbb5' : undefined }}
            onClick={() => {
              onUserInfoChange((draft) => {
                const selectedSkill = draft.skills.find((s) => s.skillId === skill.skillId)
                selectedSkill!.isSelected = !selectedSkill!.isSelected
              })
            }}
            key={skill.skillId}
          >
            <div style={{ padding: 4 }}>
              {skill.name}
              <Icon style={{ marginLeft: 8 }} />
            </div>
          </Tag>
        )
      })}
    </div>
  )

  const left = (
    <div className={s.page3}>
      {searchComponent}
      {candidates}
    </div>
  )

  const footer = (
    <>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '3',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '3',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </>
  )

  return (
    <OnboardingPageLayout
      header='ABOUT ME'
      subHeader={t('onboarding.page3.skill.addTag')}
      left={left}
      footer={footer}
      percent={3}
    />
  )
}
